import { Carousel, Radio, Tag } from 'antd5';
import LazyLoad, { type LazyLoadProps } from 'react-lazyload';
import styled, { type IStyledComponent } from 'styled-components';

import SeverityTag from 'components/SeverityTag';
import { Button } from 'components/ui';
import { getSeverityColor } from 'utils/color/helpers';

export const ThumbnailContainer = styled.div<{ aspectRatio?: string }>`
  position: relative;
  overflow: hidden;
  display: block;
  aspect-ratio: ${({ aspectRatio }) => aspectRatio ?? '4/3'};
  width: 100%;
  height: 100%;
`;

export const ThumbnailImage = styled.img<{ scaleFilter: number | undefined }>`
  width: ${({ scaleFilter }) => (scaleFilter ? '100%' : 'auto')};
  max-width: ${({ scaleFilter }) => (scaleFilter ? 'none' : '100%')};
  max-height: 100%;
  object-fit: contain;
`;

export const StyledSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  max-width: 100%;
  max-height: 100%;

  &:hover {
    polygon {
      stroke-width: 0.006;
    }
  }
`;

/**
 * The observation will be drawn in the color corresponding to its severity if 4-5,
 * teal if severity < 4 or unset
 */
export const StyledPolygon = styled.polygon<{
  severity?: number | null;
  hasColorCodedObservations: boolean;
}>`
  fill: none;
  stroke-width: 0.006;
  stroke: ${({ theme, severity, hasColorCodedObservations }) =>
    hasColorCodedObservations ? getSeverityColor(theme, severity) ?? theme.teal : theme.red};
  ${({ onClick }) => (onClick ? 'cursor: pointer;' : '')};
`;

export const AttributesContainer = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  padding: 0.5rem;
`;

export const AttributesContainerZoomable = styled.span`
  align-items: center;
  display: flex;
`;

export const ButtonContainer = styled.div<{ $isInCarousel: boolean }>`
  position: absolute;
  bottom: 0;
  padding: 0.5rem;

  display: flex;
  align-items: end;
  justify-content: space-between;
  grid-gap: 0.25rem;
  width: 100%;
`;

export const ButtonGroup = styled.div`
  display: flex;
  align-items: end;
  gap: 0.25rem;
`;

export const ReviewedToggle = styled(Radio.Group)<{ $small: boolean }>`
  display: flex;

  > .ant-radio-button-wrapper {
    align-items: center;
    display: flex;
    height: 32px;
    justify-content: center;
    width: ${({ $small }) => ($small ? 62 : 92)}px;

    > .ant-radio-button-label {
      font-size: ${({ $small }) => ($small ? 10 : 14)}px;
    }
  }

  .anticon {
    font-size: 1rem;
  }
`;

// TODO: swap this for new component
export const StyledSeverity = styled(SeverityTag)`
  margin-right: 0.25rem !important;
`;

export const DistanceTag = styled(Tag)`
  margin: 0 !important;
  color: ${({ theme }) => theme.secondaryBackgroundColor} !important;
  background: ${({ theme }) => theme.textColor} !important;
`;

export const CarouselContainer = styled.div`
  height: 100%;
  position: relative;

  .ant-carousel {
    height: 100%;
  }

  > button {
    height: 49px;
    position: absolute;
    top: calc(50% - 24px);
    width: 14px !important;
    z-index: 1;

    &.next-button {
      right: 0;
    }

    .anticon {
      font-size: 10px !important;
      vertical-align: 0 !important;
    }
  }
`;

export const StyledCarousel = styled(Carousel)`
  height: 100%;

  .slick-slider,
  .slick-list,
  .slick-track,
  .slick-slide,
  .slick-slide > div,
  .slick-slide > div > div {
    height: 100%;
  }

  .slick-dots {
    align-items: center;
    background: ${({ theme }) => theme.carouselBackgroundColor};
    border-top: 1px solid ${({ theme }) => theme.gray};
    bottom: 0;
    display: flex;
    flex-direction: row;
    height: 24px;
    justify-content: center;
    margin: 0;
    width: 100%;

    li {
      align-items: center;
      display: flex;
      justify-content: center;
      height: 12px;
      margin: 0;
      width: 12px;

      button {
        background: ${({ theme }) => theme.teal} !important;
        border-radius: 8px;
        height: 8px;
        opacity: 0.4;
        width: 8px;
      }

      &.slick-active {
        button {
          border-radius: 12px;
          height: 12px;
          opacity: 1;
          width: 12px;
        }

        &:after {
          display: none;
        }
      }
    }
  }
`;

export const EmptyImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

export const RotationContainer = styled.div<{ degrees: number | false | undefined }>`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  transition: transform 0.5s;
  transform: ${({ degrees }) => !!degrees && `rotateZ(${degrees}deg)`};
`;

export const ExpandButton = styled(Button)<{ $isInCarousel: boolean }>`
  bottom: ${({ $isInCarousel }) => ($isInCarousel ? 'calc(24px + 0.5rem)' : '0.5rem')};
  left: 0.5rem;
  position: absolute;
  z-index: 2;
`;

export const MapButton = styled(ExpandButton)`
  left: 3rem;
`;

export const MapButtonRight = styled(ExpandButton)`
  left: auto;
  right: 0.5rem;
`;

export const ExtraContentContainer = styled.div<{ height: string; width: string }>`
  position: absolute;
  top: 0;
  height: ${({ height }) => height};
  width: ${({ width }) => width};
`;

export const LazyLoadContainer: IStyledComponent<'web', LazyLoadProps> = styled(
  LazyLoad as React.ComponentType<LazyLoadProps>
)`
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;
`;
